import { traceOf, type ElementRole } from './elementTrace';

/**
 * Rejestrator pomiaru użycia (decyzja użytkownika 22.10.2026: „pilotaż
 * będzie na produkcji, ale zbuduj pomiary na wszystkich środowiskach”;
 * przełącznik `usageTelemetry`, S-22.10-02). Od 23.10.2026 **pełny ślad
 * działań** (decyzja użytkownika: „przyciski też musimy wiedzieć
 * i generalnie wszystko, co się tam dzieje”).
 *
 * ## Co zapisuje
 *
 * - wejście na ekran — ścieżka z części adresu po `#` (bez parametrów
 *   zapytania; wzorzec z `:id` składa API) i **czas na ekranie** przy
 *   wyjściu z niego (tylko czas, gdy karta była widoczna);
 * - **kliknięcie w każdy element interaktywny** — przycisk, odnośnik,
 *   zakładkę, pozycję menu, chip, pole wyboru, przełącznik, wiersz listy:
 *   `data-testid` (z kontraktu albo nasz), jeśli jest, a do tego rola
 *   elementu, stała etykieta interfejsu i sekcja (`elementTrace.ts`);
 * - zatwierdzenie okna — główny przycisk w pasku akcji okna dialogowego;
 *   otwarcie i zamknięcie okna z tytułem (`UsageTracker`);
 * - **działanie** — każde żądanie POST/PUT/PATCH/DELETE do API (poza
 *   pomiarem i logowaniem): metoda, ścieżka, kod odpowiedzi, czas trwania;
 * - błąd odczytu API — kod, metoda i ścieżka (bez zapytania);
 * - zmiana filtru, sortowania i strony listy — **sama nazwa**, nigdy wartość;
 * - eksport (pobranie pliku) i wylogowanie.
 *
 * Nigdy: wartości pól, treści komórek, treści żądań i odpowiedzi, wartości
 * filtrów, nazw kontrahentów i kierowców. Konta, roli i działu front nie
 * podaje — dopisuje je serwer z tokenu.
 *
 * ## Zapis nigdy nie przeszkadza w pracy
 *
 * Zdarzenia idą paczkami (co 10 s albo po 50 — co pierwsze; przy zamknięciu
 * karty `fetch` z `keepalive`, bo `sendBeacon` nie niesie nagłówka
 * logowania). Każdy błąd wysyłki — sieć, 4xx, 5xx — cicho odpuszczamy, bez
 * ponawiania i bez komunikatu. Przy wyłączonym pomiarze (stan środowiska
 * z `GET /usage-events/config`) nic się nie zbiera.
 */

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'token';

/** Ścieżka API pomiaru — jej błędów i działań nie zapisujemy (inaczej pętla). */
export const USAGE_API_PATH = '/usage-events';

export type UsageKind =
  | 'screen'
  | 'click'
  | 'dialogConfirm'
  | 'apiError'
  | 'action'
  | 'dialogOpen'
  | 'dialogClose'
  | 'filter'
  | 'sort'
  | 'pagination'
  | 'export'
  | 'logout'
  | 'screenTime';

/** Zdarzenie w paczce — kształt kontraktu `POST /usage-events`. */
export interface UsageEvent {
  at: number;
  kind: UsageKind;
  path?: string;
  elementId?: string | null;
  elementRole?: ElementRole | null;
  label?: string | null;
  section?: string | null;
  recordId?: number;
  durationMs?: number;
  statusCode?: number | null;
  method?: string;
  apiPath?: string;
}

export interface UsageConfig {
  enabled: boolean;
  maxBatch: number;
  flushSize: number;
  flushIntervalMs: number;
}

/** Zanim przyjdzie konfiguracja — tyle zdarzeń czeka (wejście na pierwszy ekran). */
export const PENDING_LIMIT = 100;
/** Najwięcej zdarzeń w buforze — starsze wypadają (sieć leży, a praca trwa). */
export const BUFFER_LIMIT = 500;
/** Tyle czekamy po kliknięciu wiersza listy na przejście na kartę rekordu. */
export const ROW_RECORD_WAIT_MS = 1500;
/** Metody działań zmieniających dane. */
export const ACTION_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'] as const;
/** Parametry strony listy react-admina w adresie. */
const PAGE_KEYS = ['page', 'perPage'] as const;

/** Wysyłka paczki — podmieniana w testach. */
export type UsageSender = (body: string, keepalive: boolean) => Promise<unknown>;

const defaultSender: UsageSender = (body, keepalive) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return Promise.resolve();
  return fetch(`${API}${USAGE_API_PATH}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body,
    keepalive,
  });
};

/** Ścieżka ekranu bez zapytania i bez `#`. */
export function screenPath(path: string): string {
  const noQuery = path.split('?')[0].replace(/^#/, '');
  if (noQuery === '') return '/';
  return noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
}

/** Bieżąca ścieżka z adresu przeglądarki (react-admin prowadzi trasy w `HashRouter`). */
export function currentHashPath(): string {
  try {
    return screenPath(window.location.hash || '/');
  } catch {
    return '/';
  }
}

/** Ścieżka zapytania API bez adresu serwera i bez zapytania. */
export function apiPathOf(url: string): string | null {
  let path = url;
  if (path.startsWith(API)) path = path.slice(API.length);
  else {
    const m = /^[a-z]+:\/\/[^/]+(\/.*)?$/i.exec(path);
    if (m) path = m[1] ?? '/';
  }
  path = path.split('?')[0].split('#')[0];
  if (!path.startsWith('/')) path = `/${path}`;
  return path;
}

/** Ścieżki API, których działań nie zapisujemy: pomiar i logowanie. */
export function isExcludedApiPath(apiPath: string): boolean {
  return (
    apiPath === USAGE_API_PATH ||
    apiPath.startsWith(`${USAGE_API_PATH}/`) ||
    apiPath === '/auth' ||
    apiPath.startsWith('/auth/')
  );
}

/** Atrybut otoczki z identyfikatorem kontraktu paczki wokół elementu z naszym identyfikatorem. */
export const CONTRACT_WRAPPER_ATTR = 'data-contract-wrapper';

/**
 * Identyfikator kontraktu z otoczki elementu: najbliższa otoczka kontraktu
 * (`data-contract-wrapper`) nad elementem — także przez nasze identyfikatory
 * po drodze (pole wyboru kontrahenta ma kilka swoich). Naszych identyfikatorów
 * nie zmieniamy (czytają je scenariusze e2e), więc kontrakt paczki stoi wtedy
 * na otoczce — a pomiar liczy kroki ścieżek po kontrakcie.
 */
function wrapperIdOf(el: Element): string | null {
  const outer = el.parentElement?.closest(`[${CONTRACT_WRAPPER_ATTR}]`);
  return outer?.getAttribute('data-testid') ?? null;
}

const testIdOf = (el: Element): string | null => wrapperIdOf(el) ?? el.getAttribute('data-testid');

/**
 * Identyfikator, który `@mui/icons-material` sam dokłada każdej ikonie
 * („InfoOutlinedIcon”, „CloseIcon”) — nie nasz i nie z kontraktu. Kliknięcie
 * w ikonę przycisku dawało nazwę ikony zamiast przycisku, więc krok ścieżki
 * się nie liczył (integracja 22.10.2026, scenariusz e2e 24). Ikona z jawnie
 * nadanym identyfikatorem (bez wzorca „…Icon”) zostaje.
 */
export const isIconAutoId = (el: Element): boolean =>
  el.classList.contains('MuiSvgIcon-root') && /^[A-Z][A-Za-z0-9]*Icon$/.test(el.getAttribute('data-testid') ?? '');

/** Najbliższy element z `data-testid` nad celem kliknięcia — z pominięciem ikon MUI. */
function taggedOf(el: Element): Element | null {
  let tagged = el.closest('[data-testid]');
  while (tagged && isIconAutoId(tagged)) tagged = tagged.parentElement?.closest('[data-testid]') ?? null;
  return tagged;
}

/** Zdarzenie kliknięcia przed dopisaniem ekranu i chwili. */
export type ClickEvent = Pick<UsageEvent, 'kind' | 'elementId' | 'elementRole' | 'label' | 'section'> & {
  /** Wiersz listy — numer rekordu dopisze rejestrator po przejściu na kartę. */
  rowResource?: string;
};

/**
 * Kliknięcie → zdarzenie albo `null` (kliknięcie w tło, zwykły tekst, pole
 * tekstowe bez identyfikatora).
 *
 * - `elementId` — `data-testid` elementu albo przodka (z otoczką kontraktu,
 *   bez ikon MUI), jak przed 23.10.2026 — po nim liczą się kroki ścieżek;
 * - `elementRole`, `label`, `section` — z najbliższego elementu
 *   interaktywnego (`elementTrace.ts`); tekst pól, komórek, wierszy i opcji
 *   list nie jest czytany.
 *
 * Zatwierdzenie okna: przycisk w pasku akcji okna dialogowego, który jest
 * głównym działaniem (`dlg-ok` z kontraktu, przycisk wypełniony albo
 * `type="submit"`). Pobranie pliku (`a[download]` — eksport listy) to
 * zdarzenie `export`.
 */
export function clickEventOf(target: EventTarget | null, resource = ''): ClickEvent | null {
  if (!target || typeof (target as Element).closest !== 'function') return null;
  const el = target as Element;
  const tagged = taggedOf(el);
  const trace = traceOf(el, resource, tagged?.getAttribute('data-testid') ?? null, isIconAutoId);
  const details = {
    elementRole: trace?.elementRole ?? null,
    label: trace?.label ?? null,
    section: trace?.section ?? null,
    ...(trace?.rowResource ? { rowResource: trace.rowResource } : {}),
  };
  const button = el.closest('button, [role="button"]');
  const inDialog = button?.closest('[role="dialog"]') ?? null;
  const inActions = button?.closest('.MuiDialogActions-root') ?? null;
  if (button && inDialog && (inActions || button.getAttribute('type') === 'submit')) {
    const own = button.getAttribute('data-testid');
    const primary =
      own === 'dlg-ok' ||
      button.classList.contains('MuiButton-contained') ||
      button.getAttribute('type') === 'submit';
    if (primary) {
      // Identyfikator przycisku, elementu w nim albo otoczki; okna nie zgadujemy.
      const id =
        own !== null
          ? testIdOf(button)
          : tagged && button.contains(tagged)
            ? testIdOf(tagged)
            : wrapperIdOf(button);
      return { kind: 'dialogConfirm', elementId: id, ...details };
    }
  }
  if (!tagged && !trace) return null;
  return {
    kind: trace?.download ? 'export' : 'click',
    elementId: tagged ? testIdOf(tagged) : null,
    ...details,
  };
}

/** Parametry listy z adresu: filtry (klucze → wartość jako tekst do porównania), sortowanie, strona. */
export interface ListParams {
  filter: Record<string, string>;
  sort: string | null;
  order: string | null;
  page: Record<string, string>;
}

/**
 * Parametry listy react-admina z części zapytania adresu. Wartości filtrów
 * służą WYŁĄCZNIE do porównania „zmieniło się / nie” w przeglądarce —
 * zapisujemy samą nazwę filtru.
 */
export function listParamsOf(search: string): ListParams | null {
  const q = search.startsWith('?') ? search.slice(1) : search;
  if (q === '') return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(q);
  } catch {
    return null;
  }
  const filter: Record<string, string> = {};
  const raw = params.get('filter');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) filter[k] = JSON.stringify(v);
      }
    } catch {
      // Zły filtr w adresie — nic o nim nie wiemy.
    }
  }
  const page: Record<string, string> = {};
  for (const k of PAGE_KEYS) {
    const v = params.get(k);
    if (v !== null) page[k] = v;
  }
  const sort = params.get('sort');
  const order = params.get('order');
  if (!raw && !sort && !order && Object.keys(page).length === 0) return null;
  return { filter, sort, order, page };
}

/** Zmiany parametrów listy jako zdarzenia (same nazwy). */
export function listChanges(before: ListParams, after: ListParams): { kind: 'filter' | 'sort' | 'pagination'; label: string }[] {
  const out: { kind: 'filter' | 'sort' | 'pagination'; label: string }[] = [];
  const keys = new Set([...Object.keys(before.filter), ...Object.keys(after.filter)]);
  for (const k of Array.from(keys).sort()) {
    if (before.filter[k] !== after.filter[k]) out.push({ kind: 'filter', label: k });
  }
  if ((before.sort !== after.sort || before.order !== after.order) && after.sort) {
    out.push({ kind: 'sort', label: after.sort });
  }
  for (const k of PAGE_KEYS) {
    if (before.page[k] !== after.page[k] && after.page[k] !== undefined) out.push({ kind: 'pagination', label: k });
  }
  return out;
}

interface PendingRow {
  event: UsageEvent;
  resource: string;
  timer: ReturnType<typeof setTimeout>;
}

export class UsageRecorder {
  private config: UsageConfig | null = null;
  private pending: UsageEvent[] = [];
  private buffer: UsageEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastScreen: string | null = null;
  /** Od kiedy bieżący ekran jest widoczny (`null` — karta ukryta). */
  private screenSince: number | null = null;
  private pendingRow: PendingRow | null = null;
  private listPath: string | null = null;
  private listState: ListParams | null = null;

  constructor(
    private readonly send: UsageSender = defaultSender,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** Konfiguracja z API. Wyłączony pomiar — bufor i czekające zdarzenia znikają. */
  configure(config: UsageConfig): void {
    this.config = config;
    if (!config.enabled) {
      this.pending = [];
      this.buffer = [];
      this.stopTimer();
      return;
    }
    for (const e of this.pending) this.push(e);
    this.pending = [];
    this.startTimer();
  }

  get enabled(): boolean {
    return this.config?.enabled === true;
  }

  /** Zdarzenie do bufora; nic nie wysyła od razu poza pełną paczką. */
  record(event: Omit<UsageEvent, 'at'> & { at?: number }): void {
    try {
      const e: UsageEvent = { ...event, at: event.at ?? this.now() };
      if (this.config === null) {
        if (this.pending.length < PENDING_LIMIT) this.pending.push(e);
        return;
      }
      if (!this.config.enabled) return;
      this.push(e);
    } catch {
      // Pomiar nigdy nie przeszkadza w pracy.
    }
  }

  /**
   * Wejście na ekran — ta sama ścieżka drugi raz z rzędu się nie liczy.
   * Wyjście z poprzedniego zapisuje czas na nim; wiersz listy kliknięty
   * przed chwilą dostaje numer rekordu karty, na którą przeszliśmy.
   */
  screen(path: string): void {
    const p = screenPath(path);
    if (p === this.lastScreen) return;
    this.resolveRow(p);
    this.closeScreenTime();
    this.lastScreen = p;
    this.screenSince = this.now();
    this.record({ kind: 'screen', path: p });
  }

  /** Ostatni ekran (do kliknięć i błędów API). */
  get currentPath(): string {
    return this.lastScreen ?? currentHashPath();
  }

  click(target: EventTarget | null): void {
    try {
      const path = this.currentPath;
      const e = clickEventOf(target, path.split('/')[1] ?? '');
      if (!e) return;
      const { rowResource, ...rest } = e;
      const event: UsageEvent = { ...rest, path, at: this.now() };
      if (rowResource) this.holdRow(event, rowResource);
      else this.record(event);
    } catch {
      // Pomiar nigdy nie przeszkadza w pracy.
    }
  }

  apiError(status: number | undefined, method: string | undefined, url: string | undefined): void {
    if (typeof status !== 'number' || status < 400 || !url) return;
    const apiPath = apiPathOf(url);
    if (!apiPath || isExcludedApiPath(apiPath)) return;
    this.record({
      kind: 'apiError',
      path: this.currentPath,
      statusCode: status,
      method: (method ?? 'GET').toUpperCase(),
      apiPath,
    });
  }

  /**
   * Działanie zmieniające dane: metoda, ścieżka, kod odpowiedzi (`null` — bez
   * odpowiedzi) i czas trwania. Odczyty (GET) nie są działaniem — ich błędy
   * idą jako `apiError`.
   */
  action(method: string | undefined, url: string | undefined, status: number | null | undefined, durationMs: number): void {
    const m = (method ?? 'GET').toUpperCase();
    if (!(ACTION_METHODS as readonly string[]).includes(m) || !url) return;
    const apiPath = apiPathOf(url);
    if (!apiPath || isExcludedApiPath(apiPath)) return;
    this.record({
      kind: 'action',
      path: this.currentPath,
      method: m,
      apiPath,
      statusCode: typeof status === 'number' ? status : null,
      durationMs: Math.max(0, Math.round(durationMs)),
    });
  }

  /** Otwarcie okna dialogowego (tytuł okna jako etykieta). */
  dialogOpened(label: string | null): void {
    this.record({ kind: 'dialogOpen', path: this.currentPath, label });
  }

  /** Zamknięcie okna z czasem, przez jaki było otwarte. */
  dialogClosed(label: string | null, durationMs: number): void {
    this.record({ kind: 'dialogClose', path: this.currentPath, label, durationMs: Math.max(0, Math.round(durationMs)) });
  }

  /**
   * Zmiana adresu listy: filtr, sortowanie, strona — same nazwy. Pierwsze
   * parametry po wejściu na listę (domyślne filtry, zapamiętana strona) nie
   * są zmianą.
   */
  listLocation(pathname: string, search: string): void {
    try {
      const p = screenPath(pathname);
      const params = listParamsOf(search);
      if (p !== this.listPath) {
        this.listPath = p;
        this.listState = params;
        return;
      }
      const before = this.listState;
      this.listState = params;
      if (!before || !params) return;
      for (const c of listChanges(before, params)) this.record({ ...c, path: p });
    } catch {
      // Pomiar nigdy nie przeszkadza w pracy.
    }
  }

  /** Karta ukryta — czas na ekranie się zatrzymuje i zebrane wychodzi. */
  hidden(): void {
    this.closeScreenTime();
    this.flushRow();
    this.flush(true);
  }

  /** Karta znów widoczna — liczymy dalej czas bieżącego ekranu. */
  visible(): void {
    if (this.lastScreen !== null && this.screenSince === null) this.screenSince = this.now();
  }

  /** Wysyła czekające zdarzenia paczkami; błąd — cicho odpuszcza. */
  flush(keepalive = false): void {
    if (!this.enabled) return;
    const max = this.config?.maxBatch ?? 100;
    while (this.buffer.length > 0) {
      const events = this.buffer.splice(0, max);
      try {
        const body = JSON.stringify({ sentAt: this.now(), events });
        this.send(body, keepalive).catch(() => undefined);
      } catch {
        // Bez ponawiania — paczka przepada, praca trwa.
      }
    }
  }

  /** Wylogowanie: zdarzenie „wylogowanie”, czas ostatniego ekranu, wysyłka i koniec sesji. */
  logout(): void {
    this.record({ kind: 'logout', path: this.currentPath });
    this.endSession();
  }

  /** Koniec sesji: wyślij, co jest, i zapomnij stan konta. */
  endSession(): void {
    this.closeScreenTime();
    this.flushRow();
    this.flush(true);
    this.reset();
  }

  reset(): void {
    this.stopTimer();
    if (this.pendingRow) clearTimeout(this.pendingRow.timer);
    this.pendingRow = null;
    this.config = null;
    this.pending = [];
    this.buffer = [];
    this.lastScreen = null;
    this.screenSince = null;
    this.listPath = null;
    this.listState = null;
  }

  private closeScreenTime(): void {
    if (this.lastScreen === null || this.screenSince === null) return;
    const durationMs = Math.max(0, this.now() - this.screenSince);
    this.screenSince = null;
    this.record({ kind: 'screenTime', path: this.lastScreen, durationMs });
  }

  /** Kliknięty wiersz listy czeka chwilę na przejście na kartę rekordu. */
  private holdRow(event: UsageEvent, resource: string): void {
    this.flushRow();
    const timer = setTimeout(() => this.flushRow(), ROW_RECORD_WAIT_MS);
    this.pendingRow = { event, resource, timer };
  }

  private resolveRow(nextPath: string): void {
    const row = this.pendingRow;
    if (!row) return;
    const m = new RegExp(`^/${row.resource.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/(\\d+)(?:/|$)`).exec(nextPath);
    if (m) {
      const id = Number(m[1]);
      if (Number.isSafeInteger(id) && id > 0) row.event.recordId = id;
    }
    this.flushRow();
  }

  private flushRow(): void {
    const row = this.pendingRow;
    if (!row) return;
    clearTimeout(row.timer);
    this.pendingRow = null;
    this.record(row.event);
  }

  private push(e: UsageEvent): void {
    this.buffer.push(e);
    if (this.buffer.length > BUFFER_LIMIT) this.buffer.splice(0, this.buffer.length - BUFFER_LIMIT);
    if (this.buffer.length >= (this.config?.flushSize ?? 50)) this.flush();
  }

  private startTimer(): void {
    this.stopTimer();
    const every = this.config?.flushIntervalMs ?? 10_000;
    this.timer = setInterval(() => this.flush(), every);
  }

  private stopTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }
}

/** Jeden rejestrator na kartę przeglądarki. */
export const usageRecorder = new UsageRecorder();

/** Błąd API z dostawcy danych albo klienta HTTP — nigdy nie rzuca. */
export function recordApiError(status: unknown, method: unknown, url: unknown): void {
  try {
    usageRecorder.apiError(
      typeof status === 'number' ? status : undefined,
      typeof method === 'string' ? method : undefined,
      typeof url === 'string' ? url : undefined,
    );
  } catch {
    // Pomiar nigdy nie przeszkadza w pracy.
  }
}

/**
 * Wynik żądania do API z dostawcy danych albo klienta HTTP: działanie
 * (POST/PUT/PATCH/DELETE — zawsze, z kodem) albo błąd odczytu (GET ≥ 400).
 * Nigdy nie rzuca; treści żądania i odpowiedzi nie dotyka.
 */
export function recordApiResult(method: unknown, url: unknown, status: unknown, durationMs: number): void {
  try {
    const m = typeof method === 'string' ? method.toUpperCase() : 'GET';
    const u = typeof url === 'string' ? url : undefined;
    const s = typeof status === 'number' && status > 0 ? status : null;
    if ((ACTION_METHODS as readonly string[]).includes(m)) usageRecorder.action(m, u, s, durationMs);
    else usageRecorder.apiError(s ?? undefined, m, u);
  } catch {
    // Pomiar nigdy nie przeszkadza w pracy.
  }
}

/** Konfiguracja z API; błąd odczytu = pomiar wyłączony w tej sesji. */
export async function loadUsageConfig(): Promise<UsageConfig> {
  const off: UsageConfig = { enabled: false, maxBatch: 100, flushSize: 50, flushIntervalMs: 10_000 };
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return off;
    const res = await fetch(`${API}${USAGE_API_PATH}/config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return off;
    const d = (await res.json()) as Partial<UsageConfig>;
    const pos = (v: unknown, fallback: number) =>
      typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback;
    return {
      enabled: d.enabled === true,
      maxBatch: pos(d.maxBatch, 100),
      flushSize: pos(d.flushSize, 50),
      flushIntervalMs: pos(d.flushIntervalMs, 10_000),
    };
  } catch {
    return off;
  }
}
